/**
 * pages/admin-persons.ts —— 管理后台 · 人员 Tab（W2-3）
 * 来历：一期 [SEC:page-admin] 的 personsPanelHtml / [SEC:page-admin-write] 的 personFormModal + togglePerson，
 *      渲染层重写、交互与版式不变。
 *
 * ## 与一期的四处差别（都不要"改回去"）
 * 1. **筛选、排序、队伍 chip、参与次数、部门候选全部由服务端算好下发**（`GET /api/persons/admin`）。
 *    一期是前端 `personsVisible()` 现算的；二期前端一个都不许算（红线 4、前端纪律 2）。
 * 2. **表单多了「账号」**：二期是真登录，账号是登录名（工号只是档案信息，2026-08-05 更正）。
 *    ⚠ 2026-09-09 起真实名单的账号口径是**员工编号**，与工号同值；字段仍是两个，别合并。
 * 3. **新增可登录的人会当场返回一串随机初始密码**，只显示这一次（§五 5.3 补充 C2）。
 *    一期没有密码这件事。2026-10-28 起新增时管理员可手动指定初始密码，留空才走随机；
 *    指定了就不再弹回显窗（管理员自己知道）。强度规则仍只在服务端判。
 * 4. **多了「重置密码」**：77 人首登强制改密，必有人忘密码——这是管理员唯一的补救手段。
 *
 * ⚠ 「管理员权限」勾选框**是有意没有的**（2026-08-05 删）：管理员权限由人员类型直接决定，
 *   留着勾选框就能造出「志愿者 + 管理员」这种已被否定的组合（红线 2 的同类）。
 * ⚠ 改类型的两条**硬禁止**（队长禁改 / ≥2 支在用队禁转正式成员）由服务端拒绝，
 *   前端只负责把话说清楚——**前端禁选不算校验**（红线 4）。
 */
import type { PersonAdminItem, PersonAdminView, PersonSortKey, PersonType } from '@shared-types';
import { persons as personApi } from '../api/index.js';
import { errText } from '../api/client.js';
import { closeModal, confirmModal, emptyHtml, openModal, toast } from '../shared/common.js';
import { esc } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { TYPE_LABEL } from '../shared/nav.js';
import { state } from '../shared/state.js';
import * as meta from '../shared/meta.js';
import { refreshCurrent } from '../router/render.js';
import { afterWrite, showInitialPassword } from './admin-shared.js';

/** 类型下拉的顺序。⚠ 与服务端 TYPE_ORDER 一致：参赛角色由近到远 */
const TYPES: readonly PersonType[] = ['member', 'volunteer', 'mentor', 'external', 'executive', 'admin'];

/* ===================== 渲染（纯函数） ===================== */

const opt = (v: string, cur: string, lab: string): string =>
  `<option value="${esc(v)}"${v === cur ? ' selected' : ''}>${esc(lab)}</option>`;

/**
 * 所属队伍列：队伍号 + 队名 chip。
 * ⚠ 归档队【仍然显示】（加删除线）——不显示的话，归档队档案里成员会看不出这个人曾在里面。
 * ⚠ 管理员不入队，写「未入队」会被误读成"漏了加队伍"，直接标明归属（RULES §四）。
 */
function teamsHtml(it: PersonAdminItem): string {
  if (it.person.isAdmin) {
    return '<span class="mini" title="项目管理员不参赛、不加入任何队伍">项目组</span>';
  }
  if (it.person.type === 'executive') {
    return '<span class="mini" title="访客为全站只读身份，不加入任何队伍">不入队</span>';
  }
  if (!it.teams.length) return '<span class="dash">未入队</span>';
  /* 🔴 2026-09-14：正式成员以志愿者身份加入的队，chip 前加「志愿」并弱一档（服务端已排在正式队后面）。
     人员类型列仍只写「正式成员」——管理员靠这一列看出「他还在别的队当志愿者」（用户拍板）。 */
  return it.teams
    .map((t) => {
      const cls = [t.archived ? 'arch-t' : '', t.asVolunteer ? 'vol-t' : ''].filter(Boolean).join(' ');
      const tip = `${t.archived ? '已归档队伍' : '生效队伍'}${t.asVolunteer ? '，以志愿者身份加入（只读）' : ''}`;
      return `<span class="mini ${cls}" title="${tip}">${t.asVolunteer ? '<em>志愿</em>' : ''}${esc(t.code)} ${esc(t.name)}</span>`;
    })
    .join('');
}

/** 参与活动列。⚠ null = 不参与累计（外部导师 / 管理员），与 0 次不是一回事 */
function joinHtml(it: PersonAdminItem): string {
  if (it.joinCount === null) {
    const why =
      it.person.type === 'external'
        ? '外部导师的个人参与统计在三期'
        : `${TYPE_LABEL[it.person.type]}不参赛，参加活动按临时参与人记录，不进个人累计`;
    return `<span class="dash" title="${why}">—</span>`;
  }
  return `<span class="num${it.joinCount ? '' : ' zero'}">${it.joinCount}</span> <span class="unit">次</span>`;
}

function rowHtml(it: PersonAdminItem): string {
  const p = it.person;
  /* 队长跟在姓名后的括号里，不另起 chip：姓名列只有 10% 宽，chip 会把名字挤到换行；
     而队长是【姓名的修饰】，不是并列的独立字段（一期已定）。
     ⚠ 这里不显示「管理员」：类型列写着「项目组管理员」，重复一遍是冗余（2026-08-05 定）。 */
  const tag = it.leadTeam ? '<span class="ptag">（队长）</span>' : '';
  const leadTip = it.leadTeam
    ? `该人是 ${it.leadTeam.code} ${it.leadTeam.name}的队长，请先在队伍管理中更换队长`
    : '';
  const canReset = !!p.oaAccount && p.id !== meta.me()?.id;
  const ops =
    `<button class="tbtn" data-adm="pedit|${esc(p.id)}">编辑</button>` +
    (canReset ? `<button class="tbtn" data-adm="preset|${esc(p.id)}">重置密码</button>` : '') +
    (p.active
      ? it.leadTeam
        ? `<button class="tbtn danger" disabled title="${esc(leadTip)}">停用</button>`
        : `<button class="tbtn danger" data-adm="poff|${esc(p.id)}">停用</button>`
      : `<button class="tbtn" data-adm="pon|${esc(p.id)}">启用</button>`);

  return `
  <tr data-pid="${esc(p.id)}" class="${p.active ? '' : 'off'}">
    <td class="c-emp"><span class="p-emp">${p.empNo ? esc(p.empNo) : '<span class="dash">—</span>'}</span></td>
    <td><span class="p-name">${esc(p.name)}${tag}</span></td>
    <td><span class="mini">${TYPE_LABEL[p.type]}</span></td>
    <td class="c-dept"><span class="p-dept" ${p.type === 'external' ? 'title="所属单位"' : ''}>${esc(p.dept)}</span></td>
    <td>${teamsHtml(it)}</td>
    <td>${joinHtml(it)}</td>
    <td><span class="badge ${p.active ? 'ok' : 'off'}">${p.active ? '生效' : '已停用'}</span></td>
    <td><span class="ops">${ops}</span></td>
  </tr>`;
}

/** 可排序表头。⚠ 排序由服务端做，这里只是把当前排序状态显示出来并发起下一次请求 */
function th(key: PersonSortKey, label: string, cls?: string, tip?: string): string {
  const on = state.ad.sort === key;
  const dir = on ? (state.ad.dir === 'asc' ? '升序' : '降序') : '';
  const t = (tip ? tip + '。' : '') + label + (on ? `：当前${dir}，点击切换` : '：点击排序');
  return `<th class="${cls || ''}${on ? ' on ' + state.ad.dir : ''}" ${
    on ? `aria-sort="${state.ad.dir === 'asc' ? 'ascending' : 'descending'}"` : ''
  }>
    <button class="sortbtn" data-admsort="${key}" title="${esc(t)}">${esc(label)}${IC.up}</button></th>`;
}

export function personsPanelHtml(v: PersonAdminView): string {
  if (!v.items.length) {
    /* 两种空态分开：**筛出来是空**和**库里真没有**要说不同的话，
       否则管理员会以为「已停用」筛选坏了（一期同款）。 */
    if (!v.total) {
      return emptyHtml(
        EIC.team,
        '人员库还是空的',
        '上线前由实施人员在系统外一次性初始化人员、账号与部门；这里用于上线后逐条维护新增或变更。',
        `<button class="btn primary" data-adm="pnew">${IC.plus}新增人员</button>`,
      );
    }
    if (state.ad.st === 'off') {
      return emptyHtml(
        EIC.team,
        '当前没有已停用的人员',
        '停用取代删除——停用后历史打卡、OKR 与双周报记录全部保留',
        '<button class="tbtn" data-adm="pclear">清空筛选</button>',
      );
    }
    return emptyHtml(
      EIC.search,
      '没有匹配的人员',
      '试试换个关键词，或把筛选条件放宽',
      '<button class="tbtn" data-adm="pclear">清空筛选</button>',
    );
  }
  return `
  <table class="matrix pt">
    <colgroup><col class="pm-emp"><col class="pm-name"><col class="pm-type"><col class="pm-dept">
      <col class="pm-team"><col class="pm-join"><col class="pm-st"><col class="pm-op"></colgroup>
    <thead><tr>
      ${th('emp', '工号')}${th('name', '姓名')}${th('type', '类型')}${th('dept', '部门', 'th-dept')}
      <th>所属队伍</th>${th('join', '参与活动', undefined, '该人作为正式参与人出现在多少条打卡记录里；临时参与人不计入，外部导师的个人统计在三期')}${th('st', '状态')}<th>操作</th>
    </tr></thead>
    <tbody>${v.items.map(rowHtml).join('')}</tbody>
  </table>`;
}

export function toolbarHtml(v: PersonAdminView): string {
  return `
  <div class="toolbar">
    <div class="search">
      <span class="si" aria-hidden="true">${IC.search}</span>
      <input type="text" id="adm-q" value="${esc(state.ad.q)}" placeholder="搜索姓名、工号或部门" aria-label="搜索人员">
      <button class="sx" data-adm="pclearq" aria-label="清空搜索" style="${state.ad.q ? '' : 'display:none'}">${IC.x}</button>
    </div>
    <span class="ck-sel">类型
      <select id="adm-type" aria-label="按人员类型筛选">
        ${opt('', state.ad.type, '全部类型')}${TYPES.map((k) => opt(k, state.ad.type, TYPE_LABEL[k])).join('')}
      </select></span>
    <span class="ck-sel">状态
      <select id="adm-st" aria-label="按状态筛选">
        ${opt('', state.ad.st, '全部状态')}${opt('on', state.ad.st, '生效')}${opt('off', state.ad.st, '已停用')}
      </select></span>
    <span class="cnt">显示 <span class="num">${v.items.length}</span> / <span class="num">${v.total}</span> 人</span>
    <button class="btn primary" data-adm="pnew">${IC.plus}新增人员</button>
  </div>`;
}

/* ===================== 写操作 ===================== */

interface FormValue {
  name: string;
  type: PersonType;
  empNo: string;
  oaAccount: string;
  dept: string;
  /** 仅新增：管理员手动指定的初始密码，空串 = 用系统随机密码 */
  password: string;
}

/**
 * 新增 / 编辑人员（`it` 为 null 表示新增）。
 * ⚠ 本页只改【档案字段】：姓名 / 类型 / 账号 / 工号 / 部门。
 *   队伍归属一律走队伍管理的「管理成员」——管理后台管档案，队伍管理管关系。
 */
function personModal(v: PersonAdminView, it: PersonAdminItem | null): void {
  const isNew = it === null;
  const p = it?.person ?? null;
  const lead = it?.leadTeam ?? null;
  const init: FormValue = {
    name: p?.name ?? '',
    type: p?.type ?? 'member',
    empNo: p?.empNo ?? '',
    oaAccount: p?.oaAccount ?? '',
    dept: p?.dept ?? '',
    password: '',
  };

  const read = (root: ParentNode): FormValue => ({
    name: (root.querySelector<HTMLInputElement>('#pf-name')?.value ?? '').trim(),
    type: (root.querySelector<HTMLSelectElement>('#pf-type')?.value ?? 'member') as PersonType,
    empNo: (root.querySelector<HTMLInputElement>('#pf-emp')?.value ?? '').trim(),
    oaAccount: (root.querySelector<HTMLInputElement>('#pf-oa')?.value ?? '').trim().toLowerCase(),
    dept: (root.querySelector<HTMLInputElement>('#pf-dept')?.value ?? '').trim(),
    // ⚠ 密码不 trim：空格由服务端按规则拒绝并给文案，前端悄悄删掉会让人记错密码
    password: root.querySelector<HTMLInputElement>('#pf-pwd')?.value ?? '',
  });

  const body = `
    <div class="fld" data-f="name"><label>姓名<span class="req">*</span></label>
      <input type="text" id="pf-name" maxlength="10" value="${esc(init.name)}" placeholder="如：林深">
      <div class="err"></div></div>
    <div class="fld" data-f="type"><label>人员类型<span class="req">*</span></label>
      <select id="pf-type" ${lead ? 'disabled' : ''}>${TYPES.map((k) => opt(k, init.type, TYPE_LABEL[k])).join('')}</select>
      <div class="hint" id="pf-typehint">${
        lead
          ? `该人是 ${esc(lead.code)} ${esc(lead.name)} 的队长，类型不可修改，请先在队伍管理中更换队长。`
          : '外部导师无工号与账号、不可登录，其「部门」记所属单位。'
      }</div>
      <div class="err"></div></div>
    <div class="fld" data-f="oa"><label>账号<span class="req" id="pf-oareq">*</span></label>
      <input type="text" id="pf-oa" maxlength="64" value="${esc(init.oaAccount)}" placeholder="如：46087">
      <div class="hint"><b>账号就是登录名</b>，本训练营用<b>员工编号</b>，全库唯一。</div>
      <div class="err"></div></div>${
        isNew
          ? `
    <div class="fld" data-f="pwd"><label>初始密码</label>
      <input type="text" id="pf-pwd" maxlength="64" autocomplete="off" spellcheck="false" placeholder="留空则由系统生成随机密码">
      <div class="hint" id="pf-pwdhint">可手动指定，至少 8 位、同时含字母和数字、不含空格、不能与账号相同；<b>留空则创建后生成一串随机密码，只显示一次</b>。</div>
      <div class="err"></div></div>`
          : ''
      }
    <div class="fld" data-f="emp"><label>工号<span class="req" id="pf-empreq">*</span></label>
      <input type="text" id="pf-emp" maxlength="8" value="${esc(init.empNo)}" placeholder="如：46087">
      <div class="hint">员工编号，4—8 位数字，全库唯一；<b>只是档案信息，登录查的是上面的账号</b>。外部导师无工号。</div>
      <div class="err"></div></div>
    <div class="fld" data-f="dept"><label id="pf-deptlab">部门<span class="req">*</span></label>
      <input type="text" id="pf-dept" maxlength="20" list="pf-depts" value="${esc(init.dept)}" placeholder="可选择已有部门，也可直接输入">
      <datalist id="pf-depts">${v.depts.map((d) => `<option value="${esc(d)}"></option>`).join('')}</datalist>
      <div class="err"></div></div>`;

  const rec = openModal({
    title: isNew ? '新增人员' : `编辑人员 · ${p?.name ?? ''}`,
    body,
    footer: `<button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save>${isNew ? '创建人员' : '保存修改'}</button>`,
    dirty() {
      const val = read(rec.mask);
      return (Object.keys(init) as Array<keyof FormValue>).some((k) => val[k] !== init[k]);
    },
    onMount(root) {
      /* 类型联动（一期同款）：
           外部导师   → 工号与账号禁用置空、部门标签改「所属单位」；
           项目管理员 → 账号工号照常必填（可登录），但提示它不入队、不参赛。 */
      const sync = () => {
        const ty = (root.querySelector<HTMLSelectElement>('#pf-type')?.value ?? 'member') as PersonType;
        const ext = ty === 'external';
        for (const id of ['#pf-emp', '#pf-oa', '#pf-pwd']) {
          const el = root.querySelector<HTMLInputElement>(id);
          if (!el) continue;
          el.disabled = ext;
          if (ext) el.value = '';
        }
        const reqEmp = root.querySelector<HTMLElement>('#pf-empreq');
        const reqOa = root.querySelector<HTMLElement>('#pf-oareq');
        if (reqEmp) reqEmp.style.display = ext ? 'none' : '';
        if (reqOa) reqOa.style.display = ext ? 'none' : '';
        const lab = root.querySelector('#pf-deptlab');
        if (lab) lab.innerHTML = (ext ? '所属单位' : '部门') + '<span class="req">*</span>';
        const dept = root.querySelector<HTMLInputElement>('#pf-dept');
        if (dept) dept.placeholder = ext ? '如：清源智算科技' : '可选择已有部门，也可直接输入';
        const hint = root.querySelector('#pf-typehint');
        if (hint && !lead) {
          hint.innerHTML =
            ty === 'admin'
              ? '项目管理员拥有全局配置与数据管理权限，<b>不加入任何队伍、不参赛</b>；参加活动按临时参与人记录。'
              : ty === 'executive'
                ? '访客可登录、<b>全站只读</b>，<b>不加入任何队伍</b>，不会出现在队伍成员与打卡参与人候选里；看不到项目快报与活跃统计。'
                : '外部导师无工号与账号、不可登录，其「部门」记所属单位。';
        }
      };
      sync();
      root.querySelector('#pf-type')?.addEventListener('change', sync);
      root.querySelector('[data-save]')?.addEventListener('click', () => void submit(root));
      root.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') {
          ev.preventDefault();
          void submit(root);
        }
      });
    },
  });

  function mark(root: ParentNode, field: string, msg: string): void {
    const f = root.querySelector(`[data-f="${field}"]`);
    if (!f) return toast(msg);
    f.classList.add('bad');
    const box = f.querySelector('.err');
    if (box) box.textContent = msg;
    f.scrollIntoView({ block: 'center' });
    f.querySelector<HTMLElement>('input:not([disabled]),select:not([disabled])')?.focus();
  }

  async function save(root: ParentNode, val: FormValue): Promise<void> {
    const btn = root.querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    const input = {
      name: val.name,
      type: val.type,
      dept: val.dept,
      // ⚠ 外部导师无工号无账号：传 null，不是空串（DDL 上是唯一键，空串会撞车）
      empNo: val.type === 'external' ? null : val.empNo,
      oaAccount: val.type === 'external' ? null : val.oaAccount,
      // 只有新增、且填了才传；不传 = 服务端生成随机密码
      ...(isNew && val.type !== 'external' && val.password ? { password: val.password } : {}),
    };
    try {
      const res = isNew
        ? await personApi.createPerson(input)
        : await personApi.updatePerson(p?.id as string, input);
      closeModal(true);
      await afterWrite(
        isNew
          ? `已新增人员 ${res.person.name}（${TYPE_LABEL[res.person.type]}）${
              isNew && input.password ? '，已使用指定的初始密码' : ''
            }${
              res.person.isAdmin
                ? '，项目管理员不加入队伍'
                : res.person.type === 'executive'
                  ? '，访客为全站只读身份，不加入队伍'
                  : '，加入队伍请到队伍管理页的「管理成员」'
            }`
          : `已更新 ${res.person.name} 的人员档案`,
      );
      // ⚠ 初始密码只有这一次：afterWrite 之后再弹，否则会被重渲染盖掉
      if (res.initialPassword) showInitialPassword(res.person.name, res.initialPassword);
    } catch (e) {
      // ⚠ 错误留在弹窗里，不 toast：填的内容还在，人要在原地改
      const msg = errText(e);
      mark(root, isNew && msg.includes('密码') ? 'pwd' : 'name', msg);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function submit(root: ParentNode): Promise<void> {
    const val = read(root);
    root.querySelectorAll('.fld').forEach((f) => f.classList.remove('bad'));

    /* ⚠ 前端只拦「明显填错」，**不是校验**：格式、重名、改类型的两条硬禁止都由服务端判，
       失败文案直接用服务端返回的（红线 4 / 前端纪律 2）。 */
    const ext = val.type === 'external';
    if (!val.name) return mark(root, 'name', '请填写姓名');
    if (val.name.length < 2) return mark(root, 'name', '姓名至少 2 个字');
    if (!ext && !val.oaAccount) return mark(root, 'oa', '请填写账号');
    if (!ext && !val.empNo) return mark(root, 'emp', '请填写工号');
    if (!val.dept) return mark(root, 'dept', ext ? '请填写所属单位' : '请填写部门');

    if (isNew || !p || val.type === init.type) return save(root, val);

    /* 改类型的四条【要二次确认】的路径（RULES §四）。
       ⚠ 服务端会真的执行清账号 / 清队伍，这里必须把后果说清楚——只弹窗不落地或只落地不提示都不行。 */
    const activeTeams = (it?.teams ?? []).filter((t) => !t.archived);
    const ok = (title: string, html: string, danger = true): void => {
      confirmModal({ title, okText: '确认变更', danger, html, onOk: () => void save(root, val) });
    };

    if (val.type === 'admin') {
      return ok(
        '变更为项目管理员',
        `项目管理员<b>不参赛、不加入任何队伍</b>，保存后 <b>${esc(val.name)}</b>
         ${activeTeams.length ? `将退出 <b>${activeTeams.length}</b> 支队伍（关联被清空）` : '的队伍关联保持为空'}，
         并<b>立即获得全局配置与数据管理权限</b>。是否继续？`,
      );
    }
    if (val.type === 'executive') {
      return ok(
        '变更为访客',
        `访客为<b>全站只读</b>身份、<b>不加入任何队伍</b>，保存后 <b>${esc(val.name)}</b>
         ${activeTeams.length ? `将退出 <b>${activeTeams.length}</b> 支队伍（关联被清空）` : '的队伍关联保持为空'}${
           init.type === 'admin' ? '，并<b>失去全部管理员权限</b>' : ''
         }。是否继续？`,
      );
    }
    if (init.type === 'admin') {
      return ok(
        '变更人员类型',
        `改为<b>${TYPE_LABEL[val.type]}</b>后，<b>${esc(val.name)}</b> 将<b>失去全部管理员权限</b>，
         不能再维护周期、人员与全局配置。是否继续？`,
      );
    }
    if (ext) {
      return ok(
        '变更为外部导师',
        `外部导师<b>无工号、不可登录</b>，保存后工号 <b>${esc(init.empNo || '—')}</b> 与
         账号 <b>${esc(init.oaAccount || '—')}</b> 将被清除且<b>不可恢复</b>，登录凭据一并作废。是否继续？`,
      );
    }
    if (init.type === 'member' && activeTeams.length) {
      const t = activeTeams[0];
      return ok(
        '变更人员类型',
        `改为<b>${TYPE_LABEL[val.type]}</b>后，<b>${esc(val.name)}</b> 将从
         <b>${esc(t.code)} ${esc(t.name)}</b> 的正式成员变为支持人员，该队正式成员少 1 人。是否继续？`,
        false,
      );
    }
    return save(root, val);
  }
}

/**
 * 停用 / 启用。
 * ⚠ 停用取代删除：物理删除会让打卡的参与人、周报的编辑人、KR 的负责人全变成悬空引用。
 * ⚠ 队长不可停用——按钮本身就是禁用的，这里再挡一次（服务端还有第三道）。
 */
function togglePerson(it: PersonAdminItem, on: boolean): void {
  const p = it.person;
  if (on) {
    confirmModal({
      title: '启用人员',
      okText: '确认启用',
      html: `确认启用 <b>${esc(p.name)}</b>？<br>启用后该人重新计入正式人员统计，也可以被加入队伍。`,
      onOk: () => void run(true),
    });
    return;
  }
  if (it.leadTeam) {
    return void toast(`该人是 ${it.leadTeam.code} ${it.leadTeam.name}的队长，请先在队伍管理中更换队长`);
  }
  const teams = it.teams.filter((t) => !t.archived);
  confirmModal({
    title: '停用人员',
    okText: '确认停用',
    danger: true,
    html: `确认停用 <b>${esc(p.name)}</b>（${TYPE_LABEL[p.type]}）？<br>
      停用后该人不再计入人员统计、不能被加入队伍，<b>已登录的会话会立即失效</b>；
      ${teams.length ? `与 <b>${teams.map((t) => esc(t.code + ' ' + t.name)).join('、')}</b> 的关联保留，` : ''}
      其历史打卡、OKR 编辑与双周报提交记录<b>全部保留</b>。`,
    onOk: () => void run(false),
  });

  async function run(active: boolean): Promise<void> {
    try {
      await personApi.setPersonActive(p.id, active);
      await afterWrite(active ? `已启用 ${p.name}` : `已停用 ${p.name}，历史记录保留`);
    } catch (e) {
      toast(errText(e));
    }
  }
}

/**
 * 重置密码（§五 5.1）。
 * ⚠ 会踢掉该人全部会话——被重置往往正是因为怀疑账号被盗。
 * ⚠ 新密码只显示这一次。
 */
function resetPassword(it: PersonAdminItem): void {
  const p = it.person;
  confirmModal({
    title: '重置密码',
    okText: '确认重置',
    danger: true,
    html: `确认重置 <b>${esc(p.name)}</b>（${esc(p.oaAccount ?? '')}）的密码？<br>
      系统会生成一串随机初始密码<b>并只显示一次</b>，该人当前已登录的会话会<b>立即失效</b>，
      请提醒本人登录后自行修改。`,
    onOk: () => {
      void (async () => {
        try {
          const res = await personApi.resetPassword(p.id);
          showInitialPassword(p.name, res.initialPassword, true);
        } catch (e) {
          toast(errText(e));
        }
      })();
    },
  });
}

/* ===================== 筛选与排序（只改 state，再让 render 重取） ===================== */

let searchTimer: ReturnType<typeof setTimeout> | null = null;
/** 下一次渲染后要不要把光标放回搜索框。⚠ 输入触发重渲染会丢焦点，靠它补回来 */
let refocusSearch = false;

/** ⚠ 搜索防抖：每敲一个字都发请求，106 人的库不至于卡，但日志会被刷满 */
function onSearchInput(value: string): void {
  state.ad.q = value;
  refocusSearch = true;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => refreshCurrent(), 250);
}

function clearFilters(): void {
  state.ad.q = '';
  state.ad.type = '';
  state.ad.st = '';
  refreshCurrent();
}

/* ===================== 对外（供 pages/admin.ts 装配） ===================== */

export const load = (signal: AbortSignal): Promise<PersonAdminView> =>
  personApi.adminView(
    {
      q: state.ad.q,
      type: state.ad.type as PersonType | '',
      active: state.ad.st as 'on' | 'off' | '',
      sort: state.ad.sort as PersonSortKey,
      dir: state.ad.dir,
    },
    { signal },
  );

export const toolbar = (v: PersonAdminView): string => toolbarHtml(v);

/**
 * ⚠⚠ id / data 属性一律用 `adm-` 前缀，**绝对不要用 `ad-`**（2026-08-23 线上踩过）：
 *   广告拦截插件（uBlock / AdBlock 等）有一批通用规则专打 `#ad-*` 与 `[data-ad]`，
 *   会给元素挂 `display:none`。表现极具迷惑性——**接口正常、数据正常、控制台无报错**，
 *   工具条的「显示 21 / 21 人」照常显示，只有表格那块 DOM 不可见（rect 全 0）。
 *   排查方法：无痕窗口打开一次就知道是不是插件。周期 Tab 的 panel 同理。
 */
export const panel = (v: PersonAdminView): string =>
  `<div class="panel" id="adm-panel">${personsPanelHtml(v)}</div>`;

/** 渲染之后：把光标还给搜索框（输入触发的重渲染会丢焦点） */
export function mounted(): void {
  if (!refocusSearch) return;
  refocusSearch = false;
  const el = document.querySelector<HTMLInputElement>('#adm-q');
  if (!el) return;
  el.focus();
  // 光标放到末尾，否则继续输入会从头插入
  el.setSelectionRange(el.value.length, el.value.length);
}

export function onEvent(e: Event, v: PersonAdminView): void {
  const t = e.target;
  if (!(t instanceof Element)) return;

  if (e.type === 'input' && t.id === 'adm-q') {
    return onSearchInput((t as HTMLInputElement).value);
  }
  if (e.type === 'change' && (t.id === 'adm-type' || t.id === 'adm-st')) {
    const val = (t as HTMLSelectElement).value;
    if (t.id === 'adm-type') state.ad.type = val;
    else state.ad.st = val;
    return refreshCurrent();
  }
  if (e.type !== 'click') return;

  /* 表头排序：同一列再点一次换方向。⚠ 排序本身在服务端做，这里只改 state 再重取 */
  /* ⚠ 2026-09-14 修：这里曾写成 `dataset.admmsort`（多一个 m，08-23 `ad-`→`adm-` 批量改名时带出来的），
     永远取到 undefined，表现是「点表头没反应、也不报错」。回归断言在 admin-persons.test.ts。 */
  const sortKey = t.closest<HTMLElement>('[data-admsort]')?.dataset.admsort;
  if (sortKey) {
    if (state.ad.sort === sortKey) state.ad.dir = state.ad.dir === 'asc' ? 'desc' : 'asc';
    else {
      state.ad.sort = sortKey;
      state.ad.dir = 'asc';
    }
    return refreshCurrent();
  }

  const act = t.closest<HTMLElement>('[data-adm]')?.dataset.adm;
  if (!act) return;
  const [cmd, id] = act.split('|');
  if (cmd === 'pnew') return personModal(v, null);
  if (cmd === 'pclear' || cmd === 'pclearq') {
    if (cmd === 'pclearq') {
      state.ad.q = '';
      refocusSearch = true;
      return refreshCurrent();
    }
    return clearFilters();
  }

  const it = v.items.find((x) => x.person.id === id);
  if (!it) return;
  if (cmd === 'pedit') return personModal(v, it);
  if (cmd === 'poff') return togglePerson(it, false);
  if (cmd === 'pon') return togglePerson(it, true);
  if (cmd === 'preset') return resetPassword(it);
}
